import { createAsyncThunk } from "@reduxjs/toolkit";
import fetchAPI from "../../utils/axios";

/**
 * GraphQL Query for fetching startup contents
 * This query is executed after user login to fetch user data, subscription info, features, etc.
 */
const STARTUP_CONTENTS_QUERY = `
query StartupContents {
  startupContents(input: { appContext: "" }) {
    userInfo {
      id
      name
      group
      education
      email
      active
      isEmailVerified
    }
    userSubscription {
      id
      userId
      subscriptionType
      startDate
      endDate
      isActive
      isTrial
    }
    featureToggle {
      userId
      paragraphGenerator
      paragraphScanner
      freeBooks
      booksOnDemand
    }
    booksBySubscription {
      id
      title
      author
      level
      imageUrl
      categories
      ratings
      availability
    }
    applicationLinks {
      privacyPolicy
      termsAndConditions
      subscription
      bookStore
    }
    appSupportContacts {
      generalQuestions {
        phone
        email
      }
      customerSupport {
        phone
        email
      }
      billingAndPayments {
        phone
        email
      }
    }
    readingAssessment {
      isOnBoardingAssessmentSubmitted
    }
    learnAboutFeature {
      featureKey
      pageName
      title
      message
      content {
        id
        paragraph
      }
      actions {
        type
        target
        description
      }
      cta {
        label
        deepLink
      }
    }
    featureUsage {
      featureKey
      isUsed
    }
  }
}
`;

/**
 * Thunk to fetch startup contents from GraphQL endpoint
 * Called after user login to get all initial data in one call
 * 
 * @async
 * @function fetchStartupContents
 * @returns {Promise<Object>} Response data containing user info, subscription, features, etc.
 */
export const fetchStartupContents = createAsyncThunk(
    'startupContents/fetch',
    async (_, thunkAPI) => {
        try {
            const defaultBaseUrl = fetchAPI.defaults.baseURL.replace('/api/v1', '')
            const response = await fetchAPI.post('/graphql',
                {
                    query: STARTUP_CONTENTS_QUERY
                },
                {
                    baseURL: defaultBaseUrl

                }
            );

            // Handle GraphQL errors
            if (response.data.errors) {
                const errorMessage = response.data.errors[0]?.message || 'GraphQL Error';
                return thunkAPI.rejectWithValue(errorMessage);
            }
            return response.data.data.startupContents;
        } catch (error) {
            let message = error.response?.data?.error || error.message || 'Failed to fetch startup contents';
            return thunkAPI.rejectWithValue(message);
        }
    },
    {
        condition: (_, { getState }) => {
            const { startupContents } = getState();
            if (startupContents?.isLoading) return false;
            if (startupContents?.hasAttemptedFetch) return false;
            return true;
        }
    }
);
